import { clsx } from "clsx";
import {
  type ScaleLinear,
  type ScaleLogarithmic,
  area,
  axisLeft,
  curveMonotoneX,
  curveStepAfter,
  extent,
  format,
  line,
  scaleLinear,
  scaleLog,
  select,
} from "d3";
import { darken } from "polished";
import { type FC, useEffect, useRef } from "react";
import { useHover } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { TriggerButton, TriggerButtonGroup, TriggerButtonIntent, TriggerButtonSize } from "widgets";

import styleTrack from "../styles/Track.module.scss";

import { type IRasterSegment, buildRasterSlices } from "../../../app-utils/rasterization-service.js";
import { conditionallyPluralizeTrackLabel } from "../../../app-utils/string.js";
import {
  type BamSummary,
  type Range,
  type Track,
  type TrackNgsCoverageConfiguration,
  type TrackNgsOrderedBamFilesData,
} from "../../../graphql.js";
import {
  type INgsCoverageRangeLimits,
  getTrackId,
  toggleNgsReadsTrack,
} from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { CANVAS_TRACK_MARGIN } from "../../../settings/genome-viewer-settings.js";
import { DEFAULT_COVERAGE_HISTOGRAM_COLOR, TRACK_SUBTYPE } from "../../../settings/settings.js";
import { ShowMessageOverTrack } from "../GenomeViewerTracksList.js";
import { TrackHeader } from "../TrackHeader.js";
import { type INgsCoverageTrackData } from "./NgsCoverageTrackLoader.js";
import { CoverageScaleType } from "./NgsTrackCustomizationDialog.js";

interface INgsCoverageTrackProps {
  range: Range;
  track: Track;
  rasterData: IRasterSegment[];
  bamFileId: number;
  interpolate?: boolean;
  trackHeight: number;
  handleCustomize?: () => void;
  visiblePileup: boolean;
  message?: string;
}

/**
 * A visualization component that renders a coverage genome browser track.
 *
 * The component creates its own SVG element.
 * The main input data is a pre-computed raster data of numeric coverage features.
 * During the re-rastering phase the profile is adapted to the current range.
 *
 */
const NgsCoverageTrack: FC<INgsCoverageTrackProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const { hoverProps, isHovered } = useHover({});

  const trackWidth = useSelector<IAppState, number>((state) => state.genomeBrowser.layout.trackWidth, shallowEqual);
  const bamFileSummary = useSelector<IAppState, BamSummary>(
    (state) => state.genomeBrowser.bamDatafiles[props.bamFileId],
    shallowEqual,
  );

  const yScaleValueRange = useSelector<IAppState, number[]>((state) => {
    // TODO use lifecycle methods or hooks to update the y axis range (partially in parent component since all the trackDataMap is required ? )
    // defaults used in case no specific data were provided
    const defaultMinLog = 0.1;
    const defaultMinLin = 0;
    const defaultMax = 1;

    const coverageConfig = props.track.trackCustomization.ngsConfiguration.coverageConfig;
    const trackDataMap = state.genomeBrowser.trackDataMap;
    let valueRange: number[];

    if (coverageConfig.yAxisScaling === CoverageScaleType.DYNAMIC_COMMON) {
      const limitValues: number[] = [];
      // retrieve the coverage value limits for all bam files currently shown and get the global min/max for them
      props.track.trackCustomization?.ngsConfiguration?.orderedBamFiles?.forEach((f: TrackNgsOrderedBamFilesData) => {
        const trackData: INgsCoverageTrackData = trackDataMap[
          getTrackId({ ...props.track, fileId: f.bamFileId, trackSubtype: TRACK_SUBTYPE.NGS_COVERAGE })
        ] as INgsCoverageTrackData;

        const fileSpecificRangeLimits: INgsCoverageRangeLimits = trackData?.ngsCoverageRangeLimits;
        if (fileSpecificRangeLimits) {
          // if there were only zero values in the raster data, this is indicated by null values in fileSpecificRangeLimits
          if (fileSpecificRangeLimits.minLog !== null) {
            // the min for linear scaling is always 0
            limitValues.push(coverageConfig.logScale ? fileSpecificRangeLimits.minLog : defaultMinLin);
            limitValues.push(fileSpecificRangeLimits.max);
          }
        }
      });
      // fill in something useful in case there were only zero values in the rasters
      if (limitValues.length === 0) {
        limitValues.push(coverageConfig.logScale ? defaultMinLog : defaultMinLin);
        limitValues.push(1);
      }
      valueRange = extent(limitValues);
    } else if (coverageConfig.yAxisScaling === CoverageScaleType.FIXED_COMMON) {
      // use the manually entered values
      valueRange = [coverageConfig.yAxisScalingMin, coverageConfig.yAxisScalingMax];
    } else if (coverageConfig.yAxisScaling === CoverageScaleType.DYNAMIC_SEPARATE) {
      const trackData: INgsCoverageTrackData = trackDataMap[
        getTrackId({ ...props.track, fileId: props.bamFileId, trackSubtype: TRACK_SUBTYPE.NGS_COVERAGE })
      ] as INgsCoverageTrackData;
      const valueRangeLimits: INgsCoverageRangeLimits = trackData?.ngsCoverageRangeLimits;
      // the min for linear scaling is always 0
      if (valueRangeLimits) {
        // beware: "0" values may occur as minLog (theoretically) and as max - replace by a useful default
        valueRange = [
          coverageConfig.logScale
            ? valueRangeLimits.minLog !== null
              ? valueRangeLimits.minLog
              : defaultMinLog
            : defaultMinLin,
          valueRangeLimits.max !== null ? valueRangeLimits.max : defaultMax,
        ];
      }
    } else {
      // fallback, should never happen
      valueRange = [defaultMinLin, defaultMax];
    }

    // We need to handle situations where the valueRange contains two identical values
    // Best guess is to increase the max value slightly...
    if (valueRange && valueRange[0] === valueRange[1]) {
      if (valueRange[0] === 0) {
        valueRange[1] = 0.1;
      } else {
        valueRange[1] = valueRange[1] + 0.1 * valueRange[1];
      }
    }

    return valueRange;
  }, shallowEqual);

  const containerRef = useRef<SVGGElement>();
  const axisRef = useRef<SVGGElement>();

  const lineElements = useRef([]);
  const areaElements = useRef([]);

  useEffect(() => {
    const coverageConfig: TrackNgsCoverageConfiguration =
      props.track.trackCustomization.ngsConfiguration.coverageConfig;

    // Cleanup old elements
    if (areaElements.current.length > 0) {
      areaElements.current.forEach((e) => {
        e.remove();
      });
      areaElements.current = [];
    }

    if (lineElements.current.length > 0) {
      lineElements.current.forEach((e) => {
        e.remove();
      });
      lineElements.current = [];
    }

    const rasterData = props.rasterData;

    if (!rasterData?.length) {
      return;
    }

    const canvasHeight = props.trackHeight - 3;

    const data = rasterData.map((s: IRasterSegment) => [props.interpolate ? s.mid : s.x, s.value]);
    if (data.length) {
      data.push([trackWidth, data[data.length - 1][1]]); // needed to make the histogram is drawn properly at the end
    }
    // build the data slices, splitting the data at y=0 values and omitting 0-only areas
    const slicedData: number[][][] = buildRasterSlices(data);

    let yScale: ScaleLinear<number, number> | ScaleLogarithmic<number, number>;
    if (coverageConfig.logScale) {
      // use clamp() to tolerate the 0 values
      yScale = scaleLog().clamp(true).range([canvasHeight, 0]).domain(yScaleValueRange).base(2).nice();
    } else {
      yScale = scaleLinear().range([canvasHeight, 0]).domain(yScaleValueRange).nice();
    }

    const interpolation = props.interpolate ? curveMonotoneX : curveStepAfter;
    const valueLine: any = line()
      .curve(interpolation)
      .x((d) => d[0])
      .y((d) => Math.round(yScale(d[1])));
    const d3Area: any = area()
      .curve(interpolation)
      .x((d) => d[0])
      .y0(canvasHeight + 1)
      .y1((d) => Math.round(yScale(d[1])));

    // Show labels with a useful number of decimals,
    // with special handling for log ranges with a start smaller one and do not exceed a certain distance
    // May require a more sophisticated model, especially for the log display
    const requiredPrecision: number =
      coverageConfig.logScale &&
      yScaleValueRange[0] < 1 &&
      Math.log10(yScaleValueRange[1]) - Math.log10(yScaleValueRange[0]) < 7
        ? Math.ceil(Math.abs(Math.log10(yScaleValueRange[0]))) + 1
        : Math.max(-1 * Math.ceil(Math.log10(yScaleValueRange[1] - yScaleValueRange[0]) - 1) + 1, 0);

    const yLabelFormat = format("." + requiredPrecision + "f");
    const yAxis = axisLeft(yScale)
      .ticks(5)
      .tickFormat((d) => yLabelFormat(d));

    // Render area and top line
    const container = select(containerRef.current);

    const config: TrackNgsOrderedBamFilesData = props.track.trackCustomization.ngsConfiguration?.orderedBamFiles?.find(
      (bamFileData) => bamFileData.bamFileId === props.bamFileId,
    );
    const color: string = config?.coverageColor || DEFAULT_COVERAGE_HISTOGRAM_COLOR;

    slicedData.forEach((s) => {
      areaElements.current.push(container.append("path").datum(s).attr("fill", color).attr("d", d3Area));
      lineElements.current.push(
        container.append("path").datum(s).attr("stroke", darken(0.3, color)).attr("d", valueLine),
      );
    });

    // Render y-axis
    select(axisRef.current).call(yAxis);

    // removing the first may lead to a very naked y scale display in log mode
    // and should also be avoided if the limits were specified explicitly (both lin and log)
    if (!coverageConfig.logScale && !(coverageConfig.yAxisScaling === CoverageScaleType.FIXED_COMMON)) {
      const tickElements = select(axisRef.current).selectAll(".tick");

      // Remove first elements
      tickElements.each(function (_d, i) {
        if (i === 0) {
          (this as any).remove();
        }
      });
    }
    select(axisRef.current).selectAll(".domain").remove();
  }, [props, trackWidth, yScaleValueRange]);

  return (
    <div className={"feature-track"}>
      <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
        <TrackHeader
          isTrackHovered={isHovered}
          title={conditionallyPluralizeTrackLabel(props.track)}
          subtitle={bamFileSummary?.bamFile?.fileName}
          hasLabel
          noLabelsBottomMargin
          trackIcons={
            <TriggerButtonGroup vertical>
              <TriggerButton
                icon={"settings"}
                tooltip={"Select BAM files and customize the y-axis"}
                label={"Configure NGS Coverage Track"}
                onPress={props.handleCustomize}
                size={TriggerButtonSize.Small}
              />
              <TriggerButton
                icon={props.visiblePileup ? "keyboard_double_arrow_up" : "keyboard_double_arrow_down"}
                intent={props.visiblePileup ? TriggerButtonIntent.Active : TriggerButtonIntent.Secondary}
                label={`${props.visiblePileup ? "Hide" : "Show"} Reads Track`}
                onPress={() => dispatch(toggleNgsReadsTrack(props.bamFileId))}
                size={TriggerButtonSize.Small}
              />
            </TriggerButtonGroup>
          }
        >
          <svg
            width="100%"
            height={props.trackHeight}
            style={{
              transform: `translate(100%, calc(${CANVAS_TRACK_MARGIN}px + 1px))`, // tick are rendered outside the track SVG + compensate track header right margin + fake the top track top margin
              overflow: "visible",
            }}
          >
            <g className="feature-track-canvas">
              <g className="axis" ref={axisRef} />
            </g>
          </svg>
        </TrackHeader>
        <div className={styleTrack.container} style={{ overflow: "visible", paddingBottom: 0 }}>
          <svg
            width="100%"
            height={props.trackHeight}
            key={`canvas-${props.track.name}`}
            style={{ flex: 1, overflow: "visible" }}
          >
            {props.message && (
              <foreignObject x="0" y="0" width={"100%"} height={props.trackHeight}>
                <ShowMessageOverTrack message={props.message} />
              </foreignObject>
            )}
            <g className="feature-track-canvas" transform={`translate(0, 1.5)`}>
              <g className="profile" transform={`translate(0.5, 0)`} ref={containerRef} />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

/*
            {props.message && (
              <text
                className="zoom-in-message"
                textAnchor="middle"
                x={(trackWidth ?? 2) / 2}
                y={0}
                dy="1em"
                key={uniqueId("zoom-in-message-")}
                width={200}
              >
                {props.message}
              </text>

              )}
 */
export { NgsCoverageTrack };
